// TODO - remove becouse of legacy

import axios from 'utils/axios'
import { useMutation } from 'react-query'
import { queryClient } from 'queryClient'
import { guardFromErrors, extractErrorInfo } from 'utils/graphqlHelpers'
import { Landing } from '../types'

/// ////////////////////////////////////////////////////////////////////////////////
// GRAPHQL
/// ////////////////////////////////////////////////////////////////////////////////

type QueryResponse = GraphQLResponse<'updateLandingFromTemplate', Landing>

const UPDATE_LANDING_FROM_TEMPLATE = `
  mutation updateLandingFromTemplate($landingId: Int!) {
    updateLandingFromTemplate(landingId: $landingId) {
      id
      name
      campaignId
      campaign {
        id
        workspaceId
        name
        status
        defaultDomain
        createdAt
        updatedAt
      }
      workspaceId
      templateId
      landingType
      deploymentPlugin
      deploymentSettingsId
      paymentPlugin
      paymentSettingsId
      fulfillmentPlugin
      fulfillmentSettingsId
      mailingPlugin
      mailingSettingsId
      alternativeMailingPlugin
      alternativeMailingSettingsId
      smsPlugin
      smsSettingsId
      version
      faviconUrl
      landingQuiz {
        id
        name
      }
      assets(ownerType: LANDING) {
        id
        name
        extension
        ownerType
        ownerId
        mimeType
        s3bucket
        s3key
        signedUrl
        createdAt
        updatedAt
      }
      autoresponders(ownerType: LANDING) {
        id
        ownerType
        ownerId
        autoresponderType
        sourceCode
        subjectSourceCode
        orderItemSourceCode
        useAlternativeEmailing
        settings {
          id
          ownerType
          ownerId
          name
          alias
          type
          value
          jsonValue
          required
          createdAt
          updatedAt
        }
        createdAt
        updatedAt
      }
      components(ownerType: LANDING) {
        id
        name
        ownerType
        ownerId
        sourceCode
        sourceStyle
        createdAt
        updatedAt
      }
      dependencies(ownerType: LANDING) {
        id
        name
        ownerType
        ownerId
        version
        createdAt
        updatedAt
      }
      pages(ownerType: LANDING) {
        id
        name
        route
        order
        sourceCode
        sourceStyle
        actionsCode
        reducerCode
        settings {
          id
          ownerType
          ownerId
          name
          alias
          type
          value
          jsonValue
          createdAt
          updatedAt
        }
        createdAt
        updatedAt
      }
      settings {
        id
        ownerType
        ownerId
        name
        alias
        type
        value
        jsonValue
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
    }
  }
`

/// ////////////////////////////////////////////////////////////////////////////////
// FUNCTION
/// ////////////////////////////////////////////////////////////////////////////////

type UpdateLandingFromTemplate = (landingId: number) => Promise<Landing>

const updateLandingFromTemplate: UpdateLandingFromTemplate = async (landingId) => {
  try {
    const {
      data: { data, errors }
    }: QueryResponse = await axios.post('/graphql', {
      query: UPDATE_LANDING_FROM_TEMPLATE,
      variables: { landingId }
    })

    guardFromErrors(errors)

    if (!data) {
      throw new Error('Response body is empty!')
    }

    const { updateLandingFromTemplate } = data
    if (!updateLandingFromTemplate) {
      throw new Error("Can't update landing!")
    }

    return updateLandingFromTemplate
  } catch (err) {
    throw new Error(extractErrorInfo(err) as string)
  }
}

/// ////////////////////////////////////////////////////////////////////////////////
// HOOK EVENTS
/// ////////////////////////////////////////////////////////////////////////////////

type UpdateLandingFromTemplateContext = { prevLanding?: Landing }

type UpdateLandingFromTemplateEvents = {
  onMutate: (landingId: number) => Promise<UpdateLandingFromTemplateContext>
  onError: (error: string, landingId: number, context: UpdateLandingFromTemplateContext) => void
  onSettled: (data?: Landing) => void
}

const updateLandingFromTemplateEvents: UpdateLandingFromTemplateEvents = {
  onMutate: async (landingId: number) => {
    await queryClient.cancelQueries(['landing', landingId])

    // Snapshot the previous value
    const prevLanding = queryClient.getQueryData<Landing>(['landing', landingId])

    // Optimistically update to the new values
    if (prevLanding) {
      queryClient.setQueryData<Landing>(['landing', landingId], {
        ...prevLanding,
        id: landingId
      })
    }

    return { prevLanding }
  },
  onError: (_err, landingId, context) => {
    if (context?.prevLanding) {
      // Restore previous version of landing on any error
      queryClient.setQueryData<Landing>(['landing', landingId], context.prevLanding)
    }
  },
  onSettled: (data) => {
    if (data?.id) {
      queryClient.invalidateQueries(['landing', data.id])
      queryClient.invalidateQueries(['landingsList'])
    }
  }
}

/// ////////////////////////////////////////////////////////////////////////////////
// HOOK
/// ////////////////////////////////////////////////////////////////////////////////

export const useUpdateLandingFromTemplate = () => useMutation(updateLandingFromTemplate, updateLandingFromTemplateEvents)
